import Vue from "vue"
import { get } from "lodash"
import CoachInfo from "./coaching/coachInfo"
import CoachGameInformationForm from "./coaching/coachGameInformationForm"
import SessionRequests from "./coaching/sessionRequests"
import SessionHistory from "./coaching/sessionHistory"
import StudentSessionRequests from "./coaching/student/sessionRequests"
import Schedule from "./coaching/schedule"
import {
  getInstaProAccessToken,
  getUser,
  updateUser,
  getCoachReportCardById,
  getCoachRequestById,
  createReferralCodes,
  getReferralStats,
} from "@/api/coaching"
import moment from "moment-timezone"

const state = () => ({
  instaProAccessToken: null,
  user: null,
  reportCard: null,
  sessionRequest: null,
  referralCodes: "",
  referrals: "",
  referralStats: null,
  selectedPeriod: "DAILY",
  periodOptions: ["DAILY", "WEEKLY", "MONTHLY", "YEARLY"],
  selectedChartStats: "CLICKS",
  loadingReferralStats: false,
})

const getters = {
  getInstaProAccessToken: state => state.instaProAccessToken,
  getInstaProUser: state => state.user,
  isInstaProCoach: state => get(state, "user.is_coach", false),
  reportCard: state => state.reportCard,
  sessionRequest: state => state.sessionRequest,
  referralCodes: state => state.referralCodes,
  referrals: state => state.referrals,
  referralStats: state => state.referralStats,
  selectedPeriod: state => state.selectedPeriod,
  periodOptions: state => state.periodOptions,
  selectedChartStats: state => state.selectedChartStats,
  loadingReferralStats: state => state.loadingReferralStats,
  historiesData: state => ({
    "CLICKS": state.referralStats.histories.map(history =>
      Number(history.click_count)
    ),
    "PURCHASES": state.referralStats.histories.map(history =>
      Number(history.purchase_count)
    ),
    "CONVERSION RATE": state.referralStats.histories.map(history =>
      Number(history.conversion_rate)
    ),
    "EARNINGS": state.referralStats.histories.map(history =>
      Number(history.earned_amount)
    ),
  }),
  statsChartCategories: state => {
    if (state.selectedPeriod === "MONTHLY") {
      return state.referralStats.histories.map(history =>
        moment.utc(history.from).format("MMM YYYY")
      )
    }
    if (state.selectedPeriod === "YEARLY") {
      return state.referralStats.histories.map(history =>
        moment.utc(history.from).format("MMM YYYY")
      )
    }
    return state.referralStats.histories.map(history =>
      moment.utc(history.from).format("MMM DD")
    )
  },
}

const mutations = {
  SET_INSTAPRO_ACCESS_TOKEN: (state, token) => {
    Vue.set(state, "instaProAccessToken", token)
  },
  SET_INSTAPRO_USER: (state, userData) => {
    Vue.set(state, "user", userData)
  },
  setReportCard: (state, card) => {
    state.reportCard = card
  },
  setSessionRequest: (state, request) => {
    state.sessionRequest = request
  },
  setReferralCodes: (state, codes) => (state.referralCodes = codes),
  setReferrals: (state, referrals) => (state.referrals = referrals),
  setReferralStats: (state, referralStats) =>
    (state.referralStats = referralStats),
  setSelectedPeriod: (state, period) => (state.selectedPeriod = period),
  setSelectedChartStats: (state, selectedChartStats) =>
    (state.selectedChartStats = selectedChartStats),
  setLoadingReferralStats: (state, loading) =>
    (state.loadingReferralStats = loading),
}

const actions = {
  async loadInstaProAccessToken ({ state, commit }) {
    if (state.instaProAccessToken === null) {
      try {
        commit("SET_INSTAPRO_ACCESS_TOKEN", await getInstaProAccessToken())
      } catch (e) {
        console.error("Failed to fetch InstaPRO access token.")
      }
    }
    return state.instaProAccessToken
  },
  async loadInstaProUser ({ state, commit, dispatch }) {
    if (!state.user) {
      try {
        const instaProAccessToken = await dispatch("loadInstaProAccessToken")
        let userData = await getUser(instaProAccessToken)

        // Extract coach data
        const coachData = userData.coach
        delete userData["coach"]
        commit("SET_INSTAPRO_USER", userData)
        commit("User/Coaching/CoachInfo/SET_INSTAPRO_COACH", coachData, {
          root: true,
        })
      } catch (e) {
        console.error("Failed to fetch InstaPRO user.")
      }
    }
  },
  async forceLoadInstaProUser ({ dispatch, commit }) {
    try {
      const instaProAccessToken = await dispatch("loadInstaProAccessToken")
      let userData = await getUser(instaProAccessToken)
      // Extract coach data
      const coachData = userData.coach
      delete userData["coach"]
      commit("SET_INSTAPRO_USER", userData)
      commit("User/Coaching/CoachInfo/SET_INSTAPRO_COACH", coachData, {
        root: true,
      })
    } catch (e) {
      console.error("Failed to fetch InstaPRO user.")
    }
  },
  async saveUser ({ state, commit }, userData) {
    commit("SET_INSTAPRO_USER", userData)
  },
  async updateUserData ({ state, dispatch }, userUpdatePayload) {
    const response = await updateUser(
      state.instaProAccessToken,
      userUpdatePayload
    )
    const updatedUserData = get(response, "result")
    if (updatedUserData) {
      await dispatch("saveUser", updatedUserData)
    }
  },
  async fetchReportCardById ({ commit, state }, id) {
    await commit("setReportCard", null)
    const data = await getCoachReportCardById(id, state.instaProAccessToken)
    if (data.success) {
      commit("setReportCard", data.result)
    }
  },
  async getCoachRequestById ({ commit, state }, id) {
    await commit("setSessionRequest", null)
    const data = await getCoachRequestById(id, state.instaProAccessToken)
    if (data.success) {
      commit("setSessionRequest", data.result)
    }
  },
  async createReferralCodes ({ commit, state, dispatch }) {
    await dispatch("loadInstaProAccessToken")
    const data = await createReferralCodes(state.instaProAccessToken)
    if (data.status === "success") {
      commit("setReferralCodes", data.data)
      commit("setReferrals", data.referrals)
    }
  },
  async getReferralStats ({ commit, state, dispatch }) {
    commit("setLoadingReferralStats", true)
    await dispatch("loadInstaProAccessToken")
    const period = state.selectedPeriod
      ? state.selectedPeriod
      : state.periodOptions[0]
    // if (state.selectedPeriod && state.selectedPeriod !== "LIFETIME") {
    //   period = state.selectedPeriod
    // }
    const data = await getReferralStats(period, state.instaProAccessToken)
    commit("setReferralStats", data.data)
    commit("setLoadingReferralStats", false)
  },
}
export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
  modules: {
    CoachInfo,
    CoachGameInformationForm,
    SessionRequests,
    SessionHistory,
    Schedule,
    Student: {
      namespaced: true,
      modules: {
        SessionRequests: StudentSessionRequests,
      },
    },
  },
}



// WEBPACK FOOTER //
// ./src/store/modules/user/coaching.js